import { useMemo } from "react";

import { DonutChart } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { CompanyInformationQuota } from "app/pages/sales-intelligence/modules/quota/types";
import { handleGainAccessRedirect } from "app/common/utils/handleGainAccessRedirect";

import { StyledHeaderText, StyledInnerDonutText } from "./styles";

const QuotaCard = ({ quota }: { quota: CompanyInformationQuota | undefined }) => {
    const { remaining, total, used, interval } = quota || {};

    const onClick = () => {
        handleGainAccessRedirect("Overviews hook", "click", "sales intelligence hooks", "Banner");
    };

    const chartData = useMemo(() => {
        if (remaining == null || total == null || used == null) {
            return [];
        }

        if (remaining === 0) {
            return [
                {
                    name: "Used",
                    id: "used",
                    y: 1,
                    visible: true,
                    color: colorsPalettes.blue[400],
                },
            ];
        }

        return [
            {
                name: "Remaining",
                id: "remaining",
                y: used / total,
                visible: true,
                color: colorsPalettes.blue[400],
            },
            {
                name: "Total",
                id: "total",
                y: remaining / total,
                visible: true,
                color: colorsPalettes.blue[50],
            },
        ];
    }, [remaining, total, used]);

    return (
        <Flex
            justifyContent="space-between"
            padding="6px 14px"
            background={colorsPalettes.carbon[0]}
            borderRadius="6px"
            boxShadow="0px 3px 6px 0px #0E1E3E14"
            margin="0 0 32px"
        >
            <Stack
                gap="12px"
                alignItems="flex-start"
                margin="38px 10px 28px"
            >
                <StyledHeaderText
                    fontVariant="h4"
                    color={colorsPalettes.carbon[500]}
                >
                    {translate("si.pages.home.card.quota.title")}
                </StyledHeaderText>
                <Text
                    fontVariant="report"
                    color={colorsPalettes.carbon[400]}
                    margin="0 0 8px"
                >
                    {translate("si.pages.home.card.quota.subtitle")}
                </Text>
                <IconButton
                    type="trial"
                    iconName="sparkles"
                    onClick={onClick}
                >
                    {translate("si.common.upgrade_now")}
                </IconButton>
            </Stack>
            {quota ? (
                <Box position="relative">
                    <StyledInnerDonutText>
                        <Text
                            fontVariant="h4"
                            color={colorsPalettes.carbon[400]}
                        >
                            {`${used}/${total}`}
                        </Text>
                        <span>{translate(`si.pages.home.card.quota.usage_limit_${interval}`)}</span>
                    </StyledInnerDonutText>
                    <DonutChart
                        data={[{ data: chartData }]}
                        config={{
                            chart: {
                                height: 194,
                                width: 194,
                                backgroundColor: "transparent",
                            },
                            tooltip: { enabled: false },
                            plotOptions: {
                                series: {
                                    enableMouseTracking: false,
                                    states: { hover: { enabled: false } },
                                    animation: true,
                                },
                            },
                        }}
                        innerSize="70%"
                        withoutTooltipsPadding={false}
                        labelsAreRotated={false}
                        shouldPresentLegend={false}
                    />
                </Box>
            ) : null}
        </Flex>
    );
};

export default QuotaCard;
